/**
 * Sentry for the two browser bundles.
 *
 * - Admin/demo SPA (main.tsx): a normal global `Sentry.init` — it is our page.
 * - Embeddable widget (widget.tsx): a SCOPED client. The widget runs inside a
 *   third-party page (botilia.gr), so it must never install global
 *   window.onerror / unhandledrejection hooks or touch a Sentry instance the
 *   host page may own. Errors are captured explicitly from the widget's own
 *   error boundary and entry points.
 *
 * The DSN is not baked into the bundles; it comes from GET /api/client-config
 * at runtime so the Docker image carries no build-time configuration.
 */
import {
  BrowserClient,
  Scope,
  defaultStackParser,
  makeFetchTransport,
  dedupeIntegration,
  functionToStringIntegration,
  linkedErrorsIntegration,
} from '@sentry/browser';
import * as SentryReact from '@sentry/react';
import { getApiBase } from './api';

export interface ClientSentryConfig {
  dsn: string;
  environment: string;
  release: string | null;
}

export async function fetchClientSentryConfig(): Promise<ClientSentryConfig | null> {
  try {
    const res = await fetch(`${getApiBase()}/client-config`, { credentials: 'omit' });
    if (!res.ok) return null;
    const body = (await res.json()) as { sentry?: ClientSentryConfig | null };
    return body.sentry && body.sentry.dsn ? body.sentry : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Admin / demo SPA                                                   */
/* ------------------------------------------------------------------ */

let appInitialised = false;

export function initAppSentry(cfg: ClientSentryConfig): void {
  if (appInitialised) return;
  appInitialised = true;
  SentryReact.init({
    dsn: cfg.dsn,
    environment: cfg.environment,
    release: cfg.release ?? undefined,
    tracesSampleRate: 0,
    initialScope: { tags: { app: 'admin-spa' } },
  });
}

/* ------------------------------------------------------------------ */
/*  Widget (scoped client, no global hooks)                            */
/* ------------------------------------------------------------------ */

export interface WidgetErrorContext {
  /** Where in the widget it happened, e.g. 'init', 'render', 'open'. */
  where: string;
  extra?: Record<string, unknown>;
}

let widgetScope: Scope | null = null;
/** Errors raised before the config fetch resolved; flushed once a client exists. */
const pendingWidgetErrors: Array<{ error: unknown; ctx: WidgetErrorContext }> = [];
const MAX_PENDING = 10;

export function initWidgetSentry(cfg: ClientSentryConfig, bundleUrl: string | null): void {
  if (widgetScope) return;
  const client = new BrowserClient({
    dsn: cfg.dsn,
    environment: cfg.environment,
    release: cfg.release ?? undefined,
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    // Only inert integrations: nothing that patches window/console/fetch.
    integrations: [dedupeIntegration(), functionToStringIntegration(), linkedErrorsIntegration()],
    tracesSampleRate: 0,
  });
  const scope = new Scope();
  scope.setClient(client);
  scope.setTag('app', 'widget');
  scope.setTag('host', typeof location !== 'undefined' ? location.hostname : 'unknown');
  if (bundleUrl) scope.setTag('bundle', bundleUrl);
  client.init();
  widgetScope = scope;
  for (const p of pendingWidgetErrors.splice(0)) captureWidgetError(p.error, p.ctx);
}

export function captureWidgetError(error: unknown, ctx: WidgetErrorContext): void {
  if (!widgetScope) {
    if (pendingWidgetErrors.length < MAX_PENDING) pendingWidgetErrors.push({ error, ctx });
    return;
  }
  try {
    widgetScope.captureException(error, {
      captureContext: { tags: { where: ctx.where }, extra: ctx.extra },
    });
  } catch {
    /* never let error reporting throw into the host page */
  }
}
